import { Injectable, inject } from '@angular/core'
import { catchError, Observable, Subject, switchMap, tap, throwError } from 'rxjs'
import { AdminInviteService, OrphanInviteService } from '@se-po/shared-data-access-services'
import { SeFeToastService } from 'se-fe-toast'
import { CentralServiceResources as cs } from 'se-resource-types'
import {
  AddGroup,
  AddGroupAction,
  AddPerson,
  AddToGroup,
  AdminInvite,
  BuyMembership,
  CancelAdminInvite,
  CancelPersonaInvite,
  EditPersona,
  EditRolesPermissions,
  EditVerifiedInfo,
  GuardianInvite,
  GuardianInviteResult,
  PersonaInvite,
  PersonaInviteResult,
  RemoveAccess,
  SendMessage
} from '../types/profile-admin.types'
import { VerifiedInformation } from 'se-resource-types/dist/lib/UserService'


@Injectable({
  providedIn: 'root'
})
export class ProfileAdminService {
  private adminInviteService = inject(AdminInviteService)
  private orphanInviteService = inject(OrphanInviteService)
  private toastService = inject(SeFeToastService)

  // Action triggers (input)
  private addPersonSubject = new Subject<AddPerson>()
  private removeAccessSubject = new Subject<RemoveAccess>()
  private editRolesPermissionsSubject = new Subject<EditRolesPermissions>()
  private editPersonaSubject = new Subject<EditPersona>()
  private editVerifiedInfoSubject = new Subject<EditVerifiedInfo>()
  private adminInviteSubject = new Subject<AdminInvite>()
  private personaInviteSubject = new Subject<PersonaInvite>()
  private guardianInviteSubject = new Subject<GuardianInvite>()
  private sendMessageSubject = new Subject<SendMessage>()
  private addToGroupSubject = new Subject<AddToGroup>()
  private addGroupSubject = new Subject<AddGroup>()
  private buyMembershipSubject = new Subject<BuyMembership>()

  public readonly addPerson$ = this.addPersonSubject.asObservable()
  public readonly removeAccess$ = this.removeAccessSubject.asObservable()
  public readonly editRolesPermissions$ = this.editRolesPermissionsSubject.asObservable()
  public readonly editPersona$ = this.editPersonaSubject.asObservable()
  public readonly editVerifiedInfo$ = this.editVerifiedInfoSubject.asObservable()
  public readonly adminInvite$ = this.adminInviteSubject.asObservable()
  public readonly personaInvite$ = this.personaInviteSubject.asObservable()
  public readonly guardianInvite$ = this.guardianInviteSubject.asObservable()
  public readonly sendMessage$ = this.sendMessageSubject.asObservable()
  public readonly addToGroup$ = this.addToGroupSubject.asObservable()
  public readonly addGroup$ = this.addGroupSubject.asObservable()
  public readonly buyMembership$ = this.buyMembershipSubject.asObservable()

  // Result observables (output)
  private addPersonResultSubject = new Subject<any>()
  private addGroupResultSubject = new Subject<AddGroupAction>()
  private editRolesPermissionsResultSubject = new Subject<any>()
  private editPersonaResultSubject = new Subject<cs.Profiles.Persona>()
  private editVerifiedInfoResultSubject = new Subject<VerifiedInformation>()
  private adminInviteResultSubject = new Subject<void>()
  private adminInviteCancelResultSubject = new Subject<string>()
  private personaInviteResultSubject = new Subject<PersonaInviteResult>()
  private personaInviteCancelResultSubject = new Subject<string>()
  private guardianInviteResultSubject = new Subject<GuardianInviteResult>()

  public readonly addPersonResult$ = this.addPersonResultSubject.asObservable()
  public readonly addGroupResult$ = this.addGroupResultSubject.asObservable()
  public readonly editRolesPermissionsResult$ = this.editRolesPermissionsResultSubject.asObservable()
  public readonly editPersonaResult$ = this.editPersonaResultSubject.asObservable()
  public readonly editVerifiedInfoResult$ = this.editVerifiedInfoResultSubject.asObservable()
  public readonly adminInviteResult$ = this.adminInviteResultSubject.asObservable()
  public readonly adminInviteCancelResult$ = this.adminInviteCancelResultSubject.asObservable()
  public readonly personaInviteResult$ = this.personaInviteResultSubject.asObservable()
  public readonly personaInviteCancelResult$ = this.personaInviteCancelResultSubject.asObservable()
  public readonly guardianInviteResult$ = this.guardianInviteResultSubject.asObservable()


  public addPerson(params: AddPerson): void {
    this.addPersonSubject.next(params)
  }

  public removeAccess(params: RemoveAccess): void {
    this.removeAccessSubject.next(params)
  }

  public editRolesPermissions(params: EditRolesPermissions): void {
    this.editRolesPermissionsSubject.next(params)
  }

  public editPersona(params: EditPersona): void {
    this.editPersonaSubject.next(params)
  }

  public editVerifiedInfo(params: EditVerifiedInfo): void {
    this.editVerifiedInfoSubject.next(params)
  }

  public adminInvite(params: AdminInvite): void {
    this.adminInviteSubject.next(params)
  }

  public personaInvite(params: PersonaInvite): void {
    this.personaInviteSubject.next(params)
  }

  public guardianInvite(params: GuardianInvite): void {
    this.guardianInviteSubject.next(params)
  }

  public cancelAdminInvite(params: CancelAdminInvite): Observable<unknown> {
    return this.adminInviteService.delete(params.orgId, params.inviteId).pipe(
      tap(() => {
        if (params.successMessage) this.toastService.success(params.successMessage)
        this.emitAdminInviteCancelResult(params.inviteId)
      }),
      catchError(error => {
        if (params.errorMessage) this.toastService.error(params.errorMessage)
        return throwError(() => error)
      })
    )
  }

  public cancelPersonaInvite(params: CancelPersonaInvite): Observable<unknown> {
    return this.orphanInviteService.findAll(parseInt(params.personId, 10)).pipe(
      switchMap(invites => {
        const invite = invites[0]
        if (!invite) throw new Error('No invite found')
        return this.orphanInviteService.destroy(invite.id)
      }),
      tap(() => {
        if (params.successMessage) this.toastService.done(params.successMessage)
        this.emitPersonaInviteCancelResult(params.personId)
      }),
      catchError(error => {
        if (params.errorMessage) this.toastService.error(params.errorMessage)
        return throwError(() => error)
      })
    )
  }

  public addGroup(params: AddGroup): void {
    this.addGroupSubject.next(params)
  }

  public addToGroup(params: AddToGroup): void {
    this.addToGroupSubject.next(params)
  }

  public buyMembership(params: BuyMembership): void {
    this.buyMembershipSubject.next(params)
  }

  // Not used until modals are implemented, current setup in snf
  public sendMessage(params: SendMessage): void {
    this.sendMessageSubject.next(params)
  }

  // Result emission methods - called by the host component when modals complete actions
  public emitAddPersonResult(result: any): void {
    this.addPersonResultSubject.next(result)
  }

  public emitAddGroupResult(result: AddGroupAction): void {
    this.addGroupResultSubject.next(result)
  }

  public emitEditRolesPermissionsResult(result: any): void {
    this.editRolesPermissionsResultSubject.next(result)
  }

  public emitEditPersonaResult(persona: cs.Profiles.Persona): void {
    this.editPersonaResultSubject.next(persona)
  }

  public emitEditVerifiedInfoResult(verifiedInfo: VerifiedInformation): void {
    this.editVerifiedInfoResultSubject.next(verifiedInfo)
  }

  public emitAdminInviteResult(): void {
    this.adminInviteResultSubject.next()
  }

  public emitAdminInviteCancelResult(inviteId: string): void {
    this.adminInviteCancelResultSubject.next(inviteId)
  }

  public emitPersonaInviteResult(personaId: string, email: string, orphanInviteId?: number): void {
    const result: PersonaInviteResult = { personaId, email, orphanInviteId }
    this.personaInviteResultSubject.next(result)
  }

  public emitPersonaInviteCancelResult(personaId: string): void {
    this.personaInviteCancelResultSubject.next(personaId)
  }

  public emitGuardianInviteResult(personaId: string): void {
    this.guardianInviteResultSubject.next({ personaId })
  }

}
